import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { toast } from "sonner";
import { Crown, MessageCircle, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Caption, Card, PageHeader, Spinner, TierBadge } from "@/components/Common";
import { Rich } from "@/components/Rich";
import { useAuth } from "@/context/AuthContext";
import { api, asList, errMsg, humanize, tierRank } from "@/lib/api";
import { openPortal, startCheckout, useBillingReturn } from "@/lib/billing";
import { maxSavePct, planId as pid, planPrice as priceOf, usePublicPlans } from "@/lib/plans";
import { BillingToggle } from "@/components/BillingToggle";
import { UpgradePlans } from "@/components/UpgradePlans";
import { FREE } from "@/components/login/PricingPeek";

export default function Upgrade() {
  const { billing, tier } = useAuth();
  const publicPlans = usePublicPlans();
  const sentView = useRef(false);
  useEffect(() => { if (sentView.current) return; sentView.current = true; api.post("/events", { type: "pricing_view" }).catch(() => {}); }, []);
  const reason = useLocation().state?.reason;
  const [busy, setBusy] = useState("");
  const [orders, setOrders] = useState([]);
  const [addon, setAddon] = useState(null);
  const [notes, setNotes] = useState("");
  const [params] = useSearchParams();
  const [yearly, setYearly] = useState(params.get("interval") === "year");
  const deepTier = (params.get("tier") || params.get("plan") || "").toLowerCase();
  useEffect(() => { sessionStorage.removeItem("rpopro_return"); if (params.get("interval")) setYearly(params.get("interval") === "year"); }, [params]);
  useBillingReturn();
  useEffect(() => { api.get("/billing/orders").then((r) => setOrders(asList(r.data, "orders"))).catch(() => {}); }, []);

  const checkout = async (product, n, interval) => { setBusy(product); if (!(await startCheckout(product, "/upgrade", n, interval))) setBusy(""); };
  const portal = async () => { setBusy("portal"); if (!(await openPortal("/upgrade"))) setBusy(""); };
  const feedback = async () => {
    setBusy("feedback");
    try { const { data } = await api.post("/marketplace/feedback-session", { notes }); toast.success(data?.message || "Feedback session requested"); setAddon(null); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!billing) return <Spinner className="h-6 w-6 text-[#D4AF37]" />;
  const plans = billing.plans?.length ? billing.plans : publicPlans;
  const ends = billing.trial_ends_at && new Date(billing.trial_ends_at).toLocaleDateString();

  return (
    <div>
      <PageHeader title="Upgrade" subtitle="One subscription, shared between the RPOPRO mobile app and the web.">
        <TierBadge tier={tier} className="text-xs" />
        <Link to="/recruiter" data-testid="talk-to-recruiter-button"
          className="inline-flex h-9 items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#33290D] px-4 text-sm font-medium text-[#F3C649] transition-colors hover:bg-[#3d3110]">
          <MessageCircle className="h-4 w-4" />Talk to a recruiter
        </Link>
        {tier !== "free" && !billing.trial_active && <Button variant="outline" onClick={portal} disabled={!!busy} data-testid="billing-portal-button">{busy === "portal" ? <Spinner /> : "Manage or cancel subscription"}</Button>}
      </PageHeader>
      {reason && <div className="mb-6 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-sm text-[#F3C649]" data-testid="upgrade-reason"><Crown className="mr-2 inline h-4 w-4" />{reason.message || `This feature requires ${reason.tier}.`}</div>}
      {billing.trial_active && <div className="mb-6 rounded-xl border border-[#27272A] bg-[#18181B] p-4 text-sm text-zinc-300" data-testid="trial-banner">Your Elite trial is active{ends && ` until ${ends}`}. Subscribe to keep Elite features after it ends.</div>}
      <HumanSupportBanner where="upgrade" />
      <BillingToggle yearly={yearly} onChange={setYearly} className="mb-6" pct={maxSavePct(plans)} />
      <UpgradePlans key={deepTier} plans={[FREE, ...plans.filter((p) => pid(p) !== "free")]} yearly={yearly} busy={busy} initialOpen={deepTier}
        status={(id) => (id === "free" && billing.trial_active ? "included" : billing.trial_active ? null : tierRank(tier) === tierRank(id) ? "current" : tierRank(tier) > tierRank(id) ? "included" : null)}
        onBuy={(id) => checkout(id, undefined, yearly ? "year" : undefined)} />

      {billing.addons?.length > 0 && (
        <section className="mt-14">
          <Caption className="mb-4">Recruiter marketplace add-ons</Caption>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {billing.addons.map((a) => (
              <Card key={pid(a)} className="flex flex-col" data-testid={`addon-${pid(a)}`}>
                <div className="flex items-start justify-between gap-3"><h3 className="font-display text-lg text-zinc-100">{a.name || a.title || humanize(pid(a))}</h3><span className="font-mono text-[#F3C649]">{priceOf(a)}</span></div>
                {a.description && <p className="mt-2 flex-1 text-sm text-zinc-400">{a.description}</p>}
                <Button variant="outline" className="mt-5" onClick={() => { setAddon(a); setNotes(""); }} data-testid={`buy-addon-${pid(a)}`}>Order</Button>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="mt-14 grid gap-6 xl:grid-cols-2">
        {tier === "elite" && (
          <Card>
            <h3 className="font-display text-lg text-zinc-100">Your monthly recruiter session</h3>
            <p className="mt-1 text-sm text-zinc-400">Included with Elite: a 30-minute 1:1 with a real recruiter every month.</p>
            <Button asChild className="mt-4" data-testid="feedback-session-button"><Link to="/recruiter">Book my session</Link></Button>
          </Card>
        )}
        <Card>
          <div className="mb-4 flex items-center gap-2"><Package className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">Orders</h3></div>
          {orders.length === 0 ? <p className="text-sm text-zinc-500">No orders yet.</p> : <div className="space-y-2" data-testid="orders-list">{orders.map((o, i) => <div key={o.order_id || i} className="rounded-lg border border-[#27272A] p-3"><Rich value={o} /></div>)}</div>}
        </Card>
      </section>

      <Dialog open={!!addon} onOpenChange={(o) => !o && setAddon(null)}>
        <DialogContent className="border-[#27272A] bg-[#18181B]" data-testid="addon-dialog">
          <DialogHeader><DialogTitle className="font-display">{addon?.name || addon?.title}</DialogTitle></DialogHeader>
          <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the recruiter should know? Target roles, companies, concerns..." className="bg-[#09090B]" data-testid="addon-notes" />
          <Button onClick={() => (pid(addon) === "__feedback" ? feedback() : checkout(pid(addon), notes))} disabled={!!busy} data-testid="addon-confirm-button">{busy ? <Spinner /> : "Continue"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
